import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useParams } from "react-router-dom";
import { Layout, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import ControlPanel from "../components/tool/ControlPanel";
import { Content } from "antd/es/layout/layout";
import { useAbilitiesByPhase } from "../api/AbilitiesAPI";
import CompetencyMap from "../components/tool/ComptencyMap";
import { useSessionCompetenciesIDs } from "../api/SessionResponsesAPI";
import { useCompetenciesByAbilityId } from "../api/CompetenciesAPI";
import Summary from "../components/tool/Summary";
import { useSessionById } from "../api/SessionsAPI";

const Session = () => {
  // Initialize all refs at the top level
  const isFirstRender = useRef(true);
  const loggedParams = useRef(false);
  const loggedSessionId = useRef(false);
  const sessionInfoRef = useRef({ logged: false, data: null });
  const abilitiesRef = useRef({ logged: false, data: null });
  const competenciesRef = useRef({ logged: false, data: null, abilityId: null });
  const selectedCompetenciesRef = useRef({ logged: false, data: null });
  const toggledCompetenciesSet = useRef(false);
  const controlPanelMemoized = useRef(false);
  const loadingReason = useRef({ logged: false });
  const fullRenderLogged = useRef(false);
  
  // First render logging
  if (isFirstRender.current) {
    console.log("1. Session component initializing");
    isFirstRender.current = false;
  }
  
  const { id, phase } = useParams();
  if (!loggedParams.current) {
    console.log("2. URL params extracted:", { id, phase });
    loggedParams.current = true;
  }
  
  const sessionId = id;
  if (!loggedSessionId.current && sessionId) {
    console.log("3. Using URL parameter for sessionId:", sessionId);
    loggedSessionId.current = true;
  }
  
  const { data: sessionInfo, isLoading: sessionInfoLoading } = useSessionById(sessionId);
  if (!sessionInfoRef.current.logged && sessionInfo && !sessionInfoLoading) {
    console.log("4. Session info loaded:", sessionInfo);
    sessionInfoRef.current = { logged: true, data: sessionInfo };
  }
  
  const [currentAbility, setCurrentAbility] = useState(null);
  const [toggledCompetencies, setToggledCompetencies] = useState([]);
  const [loadState, setLoadState] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Step 2: Retrieve abilities of the phase
  const { data: abilities = [], isLoading: abilitiesLoading } = useAbilitiesByPhase(phase, sessionId);
  if (!abilitiesRef.current.logged && abilities.length > 0 && !abilitiesLoading) {
    console.log("5. Abilities loaded:", abilities);
    abilitiesRef.current = { logged: true, data: abilities };
  }

  // Step 3: Set current ability
  useEffect(() => {
    if (abilities && abilities.length > 0) {
      // Reset current ability when phase changes
      console.log("6. Setting current ability:", abilities[0]);
      setCurrentAbility(abilities[0]);
    }
  }, [abilities, phase, sessionId]);

  // Step 4: Retrieve all competencies for the current ability
  const { data: competenciesByAbility, isLoading: competenciesLoading } = useCompetenciesByAbilityId(
    currentAbility?.id
  );
  if (!competenciesRef.current.logged && 
      competenciesByAbility && 
      !competenciesLoading && 
      currentAbility?.id && 
      competenciesRef.current.abilityId !== currentAbility.id) {
    console.log("7. Competencies loaded for ability:", currentAbility.id);
    console.log("8. Competencies data:", competenciesByAbility);
    competenciesRef.current = { 
      logged: true, 
      data: competenciesByAbility, 
      abilityId: currentAbility.id 
    };
  }

  // Step 5: Retrieve selected competencies of the session
  const { 
    selectedCompetencies, 
    isLoading: sessionCompetenciesLoading,
    refetch: refetchSelectedCompetencies
  } = useSessionCompetenciesIDs(id);
  
  if (!selectedCompetenciesRef.current.logged && 
      selectedCompetencies && 
      !sessionCompetenciesLoading) {
    console.log("9. Selected competencies loaded:", selectedCompetencies);
    selectedCompetenciesRef.current = { logged: true, data: selectedCompetencies };
  }
  
  // Step 6: Set toggled competencies based on selected competencies
  useEffect(() => {
    if (!sessionCompetenciesLoading && 
        selectedCompetencies && 
        selectedCompetencies.length > 0 && 
        !toggledCompetenciesSet.current) {
      console.log("10. Setting toggled competencies from selected competencies:", selectedCompetencies);
      setToggledCompetencies(selectedCompetencies);
      setLoadState(false);
      toggledCompetenciesSet.current = true;
    } else if (!sessionCompetenciesLoading && 
               selectedCompetencies && 
               selectedCompetencies.length === 0 && 
               !toggledCompetenciesSet.current) {
      console.log("10. No selected competencies found, initializing empty toggled competencies");
      setToggledCompetencies([]);
      setLoadState(false);
      toggledCompetenciesSet.current = true;
    }
  }, [sessionCompetenciesLoading, selectedCompetencies]);

  // Handle toggling competencies (only updates local state, no API calls)
  const handleToggleCompetency = useCallback((competencyId) => {
    console.log("Toggling competency:", competencyId);
    
    // Update local state only
    setToggledCompetencies(prev => {
      const isSelected = prev.includes(competencyId);
      
      if (isSelected) {
        console.log("Removing competency from local state:", competencyId);
        return prev.filter(id => id !== competencyId);
      } else {
        console.log("Adding competency to local state:", competencyId);
        return [...prev, competencyId];
      }
    });
    
    // Mark that there are unsaved changes
    setHasUnsavedChanges(true);
  }, []);

  // Reset the toggledCompetenciesSet ref when the session ID changes
  useEffect(() => {
    if (id) {
      console.log("Session ID changed, resetting toggledCompetenciesSet ref");
      toggledCompetenciesSet.current = false;
      refetchSelectedCompetencies();
    }
  }, [id, refetchSelectedCompetencies]);

  const MemoizedControlPanel = useMemo(
    () => {
      if (!controlPanelMemoized.current) {
        console.log("11. Memoizing ControlPanel component");
        controlPanelMemoized.current = true;
      }
      return (
        <ControlPanel
          abilities={abilities}
          phaseName={phase}
          sessionInfo={sessionInfo}
          currentAbility={currentAbility}
          setCurrentAbility={setCurrentAbility}
          toggledCompetencies={toggledCompetencies}
          setToggledCompetencies={setToggledCompetencies}
          onToggleCompetency={handleToggleCompetency}
          selectedCompetencies={selectedCompetencies}
          competenciesByAbility={competenciesByAbility}
          setHasUnsavedChanges={setHasUnsavedChanges}
        />
      );
    },
    [
      abilities, 
      phase, 
      sessionInfo, 
      currentAbility, 
      toggledCompetencies, 
      selectedCompetencies, 
      competenciesByAbility,
      handleToggleCompetency
    ]
  );

  // Prepare loading state logging
  useEffect(() => {
    if ((loadState || sessionInfoLoading || abilitiesLoading || competenciesLoading || sessionCompetenciesLoading) && 
        !loadingReason.current.logged) {
      console.log("12. Loading state active because:", {
        loadState,
        sessionInfoLoading,
        abilitiesLoading,
        competenciesLoading,
        sessionCompetenciesLoading
      });
      loadingReason.current.logged = true;
    }
  }, [loadState, sessionInfoLoading, abilitiesLoading, competenciesLoading, sessionCompetenciesLoading]);

  // Prepare full render logging
  useEffect(() => {
    if (!loadState && !sessionInfoLoading && !abilitiesLoading && !competenciesLoading && !sessionCompetenciesLoading && 
        !fullRenderLogged.current) {
      console.log("13. Ready to render full Session component");
      fullRenderLogged.current = true;
    }
  }, [loadState, sessionInfoLoading, abilitiesLoading, competenciesLoading, sessionCompetenciesLoading]);

  // Show loading spinner if any critical data is still loading
  if (loadState || sessionInfoLoading || abilitiesLoading || competenciesLoading || sessionCompetenciesLoading) {
    return (
      <div className="h-full w-full flex overflow-hidden justify-center items-center">
        <Spin indicator={<LoadingOutlined spin />} size="large" />
      </div>
    );
  }

  console.log("14. Rendering full Session component");
  
  return (
    <Layout className="h-full">
      <Content className="h-full flex items-center justify-center relative">
        <Summary toggledCompetencies={toggledCompetencies}/>
        <CompetencyMap
          sessionId={id}
          currentAbility={currentAbility}
          toggledCompetencies={toggledCompetencies}
          onToggleCompetency={handleToggleCompetency}
          competenciesByAbility={competenciesByAbility}
          setHasUnsavedChanges={setHasUnsavedChanges}
          className="relative z-0"
        />
      </Content>
      {MemoizedControlPanel}
    </Layout>
  );
};

export default Session;
